import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Building2, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

const initial = { name: "", address: "", city: "", country: "India", phone: "", email: "", currency: "INR", tax_percent: 12 };

export default function Hotels() {
    const { hotels, refreshHotels, user } = useAuth();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(initial);
    const canCreate = user?.role === "super_admin" || user?.role === "hotel_owner";

    const submit = async () => {
        try {
            await api.post("/hotels", { ...form, tax_percent: Number(form.tax_percent) });
            toast.success("Hotel created");
            setOpen(false);
            setForm(initial);
            refreshHotels();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this hotel?")) return;
        try {
            await api.delete(`/hotels/${id}`);
            toast.success("Deleted");
            refreshHotels();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <AppShell title="Hotels" subtitle="Manage properties across your organization">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <div className="text-sm text-slate-500">{hotels.length} propert{hotels.length === 1 ? "y" : "ies"}</div>
                </div>
                {canCreate && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#0A1C3E] hover:bg-[#132D5C] rounded-sm" data-testid="add-hotel-btn">
                                <Plus className="w-4 h-4 mr-1" /> Add Property
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle>New Property</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                {[
                                    ["name", "Hotel name"],
                                    ["address", "Address"],
                                    ["city", "City"],
                                    ["country", "Country"],
                                    ["phone", "Phone"],
                                    ["email", "Email"],
                                ].map(([k, l]) => (
                                    <div key={k}>
                                        <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
                                        <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" data-testid={`hotel-${k}`} />
                                    </div>
                                ))}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider text-slate-600">Currency</Label>
                                        <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider text-slate-600">Tax %</Label>
                                        <Input type="number" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className="mt-1" />
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={submit} className="bg-[#0A1C3E]" data-testid="hotel-submit">Create</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hotels.map((h) => (
                    <Card key={h.id} className="p-5 border border-slate-200 rounded-md shadow-none hover:border-[#0A1C3E] transition-colors" data-testid={`hotel-card-${h.id}`}>
                        <div className="flex justify-between items-start">
                            <div className="w-10 h-10 rounded-sm bg-[#0A1C3E] flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-[#C5B08A]" />
                            </div>
                            {canCreate && (
                                <button onClick={() => remove(h.id)} className="text-slate-400 hover:text-red-600" data-testid={`hotel-delete-${h.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="mt-4 text-lg font-display font-semibold text-slate-900">{h.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {h.city}, {h.country}
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
                            <div><div className="text-slate-500">Currency</div><div className="font-mono font-semibold">{h.currency}</div></div>
                            <div><div className="text-slate-500">Tax</div><div className="font-mono font-semibold">{h.tax_percent}%</div></div>
                        </div>
                    </Card>
                ))}
            </div>
        </AppShell>
    );
}
